import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import { Field, Form, Formik, FormikProps } from 'formik';
import { OrgDetailSchema, whiteListSchema } from '../../../../utils/validation-schema';
import React, { useEffect, useState } from 'react';

import AddIcon from '../../../../components/CustomIcons/AddIcon';
import CircularProgressBar from '../../../../components/CircularProgressBar';
import CrossIcon from '../../../../components/CustomIcons/CrossIcon';
import CustomButton from '../../../../components/CustomButton';
import CustomInput from '../../../../components/CustomInput';
import CustomLabel from '../../../../components/CustomLabel';
import CustomSwitch from '../../../../components/CustomSwitch';
import { FETCH_ORG_WHITELIST } from '../../../../data/organization/action-types';
import InlineAlert from '../../../../components/InlineAlert';
import Organization from '../../../../types/organization';
import OrganizationDetail from '../../../../types/organization-detail';
import Util from '../../../../utils/util';
import WhiteListItem from '../../../../types/whitelist-Item';
import { parsePhoneNumber } from 'libphonenumber-js/min';
import useFormikValidity from '../../../../hooks/use-formik-validity';
import { useOrganizationApi } from '../../../../data/organization/api';
import useStyles from './styles';
import { useTranslation } from 'react-i18next';

interface PhoneData {
  phone: string;
  countryCode : string;
}

interface Props {
  isReadMode: boolean;
  formikRef: React.RefObject<FormikProps<OrganizationDetail>>;
  organization: Organization;
  handleTelephonyDetailChange: (orgDetail: OrganizationDetail) => void;
  onValidityChange: (isValid: boolean) => void;
}

/**
 * React component for managing telephony settings for an organization.
 *
 * This component allows users to view, add, and remove phone numbers from the organization's
 * whitelist, and manage telephony settings including the telephony number and PIN code.
 * 
 * Phone numbers are handled in E.164 format (+[countryCode][number]).
 * - On load: splits the full E.164 number into country code and local number for display.
 * - On save: combines country code and local number back into E.164 format before sending to backend.
 *
 * @param {Props} props - The component's props.
 *   - `isReadMode` (boolean): Whether the form is in read-only mode.
 *   - `formikRef` (RefObject): Reference to the main Formik form instance.
 *   - `organization` (Organization): The organization object containing telephony details.
 *   - `handleTelephonyDetailChange` (func): Callback to propagate telephony detail changes to parent.
 *   - `onValidityChange` (func): Callback to propagate form validity changes to parent.
 * @returns {JSX.Element} - The rendered TelephonyTab component.
 */
const TelephonyTab: React.FC<Props> = (props: Props) => {

  const styles = useStyles();
  const { t } = useTranslation();
  const [orgDetail, setOrgDetail] = useState<OrganizationDetail>(props.organization?.details);
  const orgApi = useOrganizationApi();
  const apiStatus = orgApi.state.orgState.apiStatus;
  const whiteListInitialValue: PhoneData = { countryCode: '', phone: '' };
  const formikRef = React.createRef<FormikProps<PhoneData>>();


/**
 * Fetches the organization's whitelist on mount if not already loaded.
 * Also resets orgDetail by splitting the full E.164 telephony number into
 * country code and local number for display in separate input fields.
 *
 * @sideEffect - Calls orgApi.fetchOrganizationWhitelist to retrieve the whitelist.
 * @sideEffect - Calls setOrgDetail with split country code and local number.
 */
	useEffect(() => {
		if (props.organization && !props.organization.whitelist) {
			orgApi.fetchOrganizationWhitelist(props.organization);
		}
		// Reset orgDetail with split number
		const { countryCode, phone } = splitPhoneNumber(
			props.organization?.details?.organizationTelephonyNumber
		);
		setOrgDetail({
			...props.organization?.details,
			organizationTelephonyCountryCode: countryCode,
			organizationTelephonyNumber: phone,
		});
	}, []);

	/**
	 * Watches for changes in telephony number fields (country code, local number, pin code)
	 * and propagates the combined E.164 phone number to the parent component.
	 *
	 * Combines organizationTelephonyCountryCode and organizationTelephonyNumber into
	 * a full E.164 formatted number (e.g. '44' + '7911123456' → '+447911123456')
	 * before calling handleTelephonyDetailChange.
	 *
	 * Also handles stripping of leading '00' or '0' from the local number.
	 *
	 * @sideEffect - Calls props.handleTelephonyDetailChange with the combined E.164 number.
	 */
	useEffect(() => {

		const { current: formik } = props.formikRef;

		if (!formik) return;

		const { organizationTelephonyCountryCode, organizationTelephonyNumber, pinCode } = formik.values;

		props.handleTelephonyDetailChange({
			...formik.values,
			organizationTelephonyNumber: organizationTelephonyNumber,
			organizationTelephonyCountryCode: organizationTelephonyCountryCode,
			pinCode: pinCode,
		});
	}, [
		props.formikRef.current?.values.organizationTelephonyCountryCode,
		props.formikRef.current?.values.organizationTelephonyNumber,
		props.formikRef.current?.values.pinCode]);

	/**
	 * Splits a full E.164 formatted phone number into its country calling code and local number.
	 * Uses libphonenumber-js for accurate parsing.
	 *
	 * @param {string} fullNumber - The full E.164 phone number (e.g. '+447911123456').
	 * @returns {{ countryCode: string, phone: string }} - The split country code and local number.
	 *   Returns empty strings if the number is invalid or cannot be parsed.
	 *
	 * @example
	 * splitPhoneNumber('+447911123456') → { countryCode: '44', phone: '7911123456' }
	 * splitPhoneNumber('+12025551234')  → { countryCode: '1',  phone: '2025551234' }
	 * splitPhoneNumber('')              → { countryCode: '',   phone: '' }
	 */
	const splitPhoneNumber = (fullNumber: string) => {
		try {
			if (!fullNumber) return { countryCode: '', phone: '' };

			const parsed = parsePhoneNumber(fullNumber);
			return {
				countryCode: parsed.countryCallingCode,  // '44'
				phone: parsed.nationalNumber,             // '7911123456'
			};
		} catch {
			return { countryCode: '', phone: '' };
		}
	};


  /**
   * Finds the selected organization from the organization list in the state.
   * This is used to display the current whitelist.
   *
   * @returns {Organization | undefined} - The selected organization, or undefined if not found.
   */
  const selectedOrg = orgApi.state.orgState.orgList.find(
    (org) => org.details.id === props.organization.details.id
  );

  /**
   * Handles the deletion of a whitelist item.
   *
   * This function calls the API to remove the specified phone number from the organization's whitelist.
   *
   * @param {WhiteListItem} item - The whitelist item to be deleted, containing the phone number.
   * @returns {void}
   */
  const onWhiteListItemDelete = (item: WhiteListItem) => {
    orgApi.removeWhitelistNumber(props.organization, item);
  }

  /**
   * Handles the form submission for adding a new phone number to the whitelist.
   *
	 * Combines the country code and local number into E.164 format before sending to the API.
	 * Strips leading '00' or '0' from the local number if present.
	 * Resets the whitelist form after successful submission.
	 *
	 * e.g. countryCode: '44', phone: '07911123456' → '+447911123456'*
   *
   * @param {PhoneData} values - The submitted phone data, containing the phone number.
   * @param {FormikHelpers<PhoneData>} formikHelpers - Formik helper functions for form manipulation.
   * @returns {void}
   * @sideEffect - Calls orgApi.AddWhitelistNumber to add the phone number to the server.
   * @sideEffect - Resets the Formik form after submission.
   */
	const onSubmit = (values: PhoneData) => {
		const { phone, countryCode } = values;

		// Strip leading 00 or 0
		let sanitized = phone;
		if (sanitized.startsWith('00')) {
			sanitized = sanitized.substring(2);
		} else if (sanitized.startsWith('0')) {
			sanitized = sanitized.substring(1);
		}

		const fullNumber = `+${countryCode}${sanitized}`;

		orgApi.addWhitelistNumber(props.organization, fullNumber);
		formikRef.current?.resetForm();
	};

  return (
    <Box sx={styles.container}>
      <Box sx={styles.content}>
        <Formik
          innerRef={props.formikRef}
          enableReinitialize
          validateOnMount={false} 
          validateOnChange
          validationSchema={OrgDetailSchema}
          initialValues={orgDetail}
          onSubmit={_ => { () => {/* Do Nothing */ } }}>
          {({ isValid }) => {
            useFormikValidity(isValid, props.onValidityChange);
            return (
              <Form style={styles.form as React.CSSProperties}>
                <Box sx={styles.formContent}>
									<CustomLabel label={t('telephonyNumber')} />
									<Box sx={styles.phoneBox}>
										<Box sx={styles.codeBox}>
											<Field
												name='organizationTelephonyCountryCode'
												component={CustomInput}
												adornmentText={'+'}
												placeholder={t('Code')}
												readOnly={props.isReadMode}
												type='number'
											/>
										</Box>
										<Box sx={{ flex: 1 }}>
											<Field
												name='organizationTelephonyNumber'
												component={CustomInput}
												placeholder={t('telephonyNumber')}
												readOnly={props.isReadMode}
												type='number'
											/>
										</Box>
									</Box>
                  <Field
                    name='pinCode'
                    type='number'
                    label={t('telephonyPinCode')}
                    placeholder={t('telephonyPinCode')}
                    readOnly={props.isReadMode}
                    component={CustomInput}
                  />
                  <CustomLabel label={t('callOptions')} />
                  <Stack sx={styles.callOptionsRow}>   
                    <Stack sx={styles.callOption}>
                      <Field
                        name='messageTypeEnabled'
                        label={t('messageTypeSelection')}
                        readOnly={props.isReadMode}
                        component={CustomSwitch}
                      />
                    </Stack>
                    <Stack sx={styles.callOption}>
                      <Field
                        name='priorityEnabled'
                        label={t('prioritySelection')}
                        readOnly={props.isReadMode}
                        component={CustomSwitch}
                      />
                    </Stack>
                  </Stack>
                </Box>
              </Form>
            )
          }}
        </Formik>
        <CustomLabel label={t('whitelistedNumbers')} />
        <InlineAlert message={orgApi.state.orgState.apiStatus?.error} />
        <Formik
          initialValues={whiteListInitialValue}
          validationSchema={whiteListSchema}
          enableReinitialize
          innerRef={formikRef}
          onSubmit={onSubmit}
        >
          {({ dirty, isValid, setFieldValue, setFieldTouched, values, errors, touched }) => (
            <Form style={styles.whiteListform as React.CSSProperties}>
              <Box sx={styles.phoneBox}>
                <Box sx={styles.codeBox}>
              <Field
                name='countryCode'
                component={CustomInput}
                adornmentText={'+'}
                type='number'
              /> 
              </Box >
              <Box sx={{ flex: 1 }}> 
               <Field
                name='phone'
                component={CustomInput}
                placeholder={t('telephoneNumber')}
                type='number'
              /> 
              </Box>
            </Box>
              <Box sx={styles.btnWrapper}>
                <CustomButton
                  type='submit'
                  title={t('add')}
                  color='primary'
                  startIcon={<AddIcon />}
                  disabled={!dirty || !isValid}
                />
              </Box>
            </Form>
          )}
        </Formik>
        <Box sx={styles.chipWrapper}>
          {selectedOrg?.whitelist?.length ? (
            selectedOrg.whitelist.map((item, index) => (
              <Chip
                key={`whitelist-chip-${index}`}
                label={item.phoneNumber}
                onDelete={() => onWhiteListItemDelete(item)}
                deleteIcon={<CrossIcon />}
                sx={styles.chip}
              />
            ))
          ) : (
            <Typography variant='p1'>{t('noWhitelist')}</Typography>
          )}
        </Box>
      </Box>
      <CircularProgressBar show={Util.isApiLoading([FETCH_ORG_WHITELIST], apiStatus)} />
    </Box>
  )
};

export default TelephonyTab;


